import { useEffect, useRef, useState } from 'react';
import { FiArrowLeft, FiLoader, FiMessageSquare, FiSend } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useHubSpotPortals } from '../hooks/useHubSpotPortals';
import { configurationAgentApi } from '../services/api';

const MAX_POLL_MS = 60000;
const POLL_INTERVAL_MS = 2000;

export default function ConfigurationAgent() {
  const { selectedPortal } = useHubSpotPortals();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!selectedPortal?.portal_id) {
      setSessionId(null);
      setMessages([]);
      return;
    }

    const storageKey = `configuration-agent-session-${selectedPortal.portal_id}`;
    const savedSession = window.sessionStorage.getItem(storageKey);
    const savedMessages = window.sessionStorage.getItem(`${storageKey}-messages`);

    if (savedSession) {
      setSessionId(savedSession);
    } else {
      setSessionId(null);
    }

    if (savedMessages) {
      try {
        setMessages(JSON.parse(savedMessages));
      } catch {
        setMessages([]);
      }
    } else {
      setMessages([]);
    }
  }, [selectedPortal?.portal_id]);

  useEffect(() => {
    if (!selectedPortal?.portal_id) return;
    const storageKey = `configuration-agent-session-${selectedPortal.portal_id}`;

    if (sessionId) {
      window.sessionStorage.setItem(storageKey, sessionId);
    }

    window.sessionStorage.setItem(`${storageKey}-messages`, JSON.stringify(messages));
  }, [selectedPortal?.portal_id, sessionId, messages]);

  useEffect(() => {
    return () => {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
      }
    };
  }, []);

  const appendMessage = (role, content) => {
    setMessages((prev) => [...prev, { role, content, createdAt: new Date().toISOString() }]);
  };

  const stopPolling = () => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const handleSend = async (event) => {
    event.preventDefault();

    const message = input.trim();
    if (!message) return;

    if (!selectedPortal?.portal_id) {
      setError('Select a portal before using the Configuration Agent.');
      return;
    }

    setError(null);
    setLoading(true);
    setStatusMessage('Submitting request...');
    appendMessage('user', message);
    setInput('');

    try {
      const response = await configurationAgentApi.startChat({
        portalId: selectedPortal.portal_id,
        sessionId,
        message,
        context: {
          portalDomain: selectedPortal.hub_domain,
        },
      });

      const jobId = response?.outputFields?.jobId || response?.jobId;
      const returnedSessionId = response?.outputFields?.sessionId || response?.sessionId;
      if (returnedSessionId) {
        setSessionId(returnedSessionId);
      }

      if (!jobId) {
        throw new Error('No jobId returned from configuration chat endpoint');
      }

      const startedAt = Date.now();
      setStatusMessage('Generating configuration guidance...');

      pollRef.current = window.setInterval(async () => {
        const elapsed = Date.now() - startedAt;
        if (elapsed >= MAX_POLL_MS) {
          stopPolling();
          setLoading(false);
          setStatusMessage(null);
          setError('Timed out waiting for response. Please try again.');
          return;
        }

        try {
          const jobStatus = await configurationAgentApi.getChatJobStatus(jobId);
          const fields = jobStatus?.outputFields || {};
          const status = fields.status;

          if (status === 'completed') {
            stopPolling();
            setLoading(false);
            setStatusMessage(null);
            appendMessage('assistant', fields.assistantMessage || 'No response generated.');
            return;
          }

          if (status === 'failed') {
            stopPolling();
            setLoading(false);
            setStatusMessage(null);
            setError(fields.errorMessage || 'Configuration chat failed.');
          }
        } catch (pollError) {
          console.error('Polling configuration chat job failed:', pollError);
        }
      }, POLL_INTERVAL_MS);
    } catch (submitError) {
      console.error('Failed to start configuration chat:', submitError);
      setLoading(false);
      setStatusMessage(null);
      setError('Failed to start configuration chat. Please try again.');
    }
  };

  const handleReset = () => {
    stopPolling();
    setMessages([]);
    setError(null);
    setStatusMessage(null);
    setLoading(false);
    setInput('');

    if (selectedPortal?.portal_id) {
      const storageKey = `configuration-agent-session-${selectedPortal.portal_id}`;
      window.sessionStorage.removeItem(storageKey);
      window.sessionStorage.removeItem(`${storageKey}-messages`);
    }

    setSessionId(null);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Link to="/agents" className="flex items-center text-gray-500 hover:text-gray-700 mr-4">
            <FiArrowLeft className="w-4 h-4 mr-1" />
            Back to Agents
          </Link>
          <div>
            <div className="flex items-center mb-1">
              <FiMessageSquare className="w-6 h-6 text-blue-600 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">Configuration Agent</h1>
            </div>
            <p className="text-gray-600">Chat with an AI guide to configure routing and setup in Distributely.</p>
          </div>
        </div>

        {selectedPortal ? (
          <p className="text-sm text-blue-700">
            Active portal: <span className="font-semibold">{selectedPortal.hub_domain}</span>
            <span className="ml-1 text-gray-600">(ID: {selectedPortal.portal_id})</span>
          </p>
        ) : (
          <p className="text-sm text-red-600">No active portal selected. Choose a portal from Dashboard first.</p>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Conversation</h2>
          <button
            type="button"
            onClick={handleReset}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Reset Session
          </button>
        </div>

        <div className="h-[440px] overflow-y-auto p-6 bg-gray-50">
          {messages.length === 0 ? (
            <div className="text-sm text-gray-500">
              Ask about your routing use case and the agent will recommend exact setup steps.
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div
                  key={`${message.createdAt}-${index}`}
                  className={`max-w-3xl rounded-lg px-4 py-3 whitespace-pre-wrap ${
                    message.role === 'user'
                      ? 'bg-blue-600 text-white ml-auto'
                      : 'bg-white border border-gray-200 text-gray-800'
                  }`}
                >
                  {message.content}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 p-4">
          {loading && statusMessage && (
            <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
              <div className="flex items-center gap-2 text-sm text-blue-700">
                <FiLoader className="h-4 w-4 animate-spin" />
                <span>{statusMessage}</span>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-blue-100">
                <div className="h-full w-1/2 animate-pulse rounded-full bg-blue-500" />
              </div>
            </div>
          )}
          {!loading && statusMessage && <p className="text-sm text-gray-500 mb-3">{statusMessage}</p>}
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

          <form onSubmit={handleSend} className="flex items-center gap-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={loading || !selectedPortal}
              placeholder="Describe your use case and constraints..."
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
            <button
              type="submit"
              disabled={loading || !selectedPortal || !input.trim()}
              className="inline-flex items-center bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg"
            >
              <FiSend className="w-4 h-4 mr-2" />
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
